import { apiClient } from "@/lib/api";
import {
  activitiesFromApi,
  activityFromApi,
  type ActivityApiPayload,
} from "@/lib/activityFromApi";
import { fetchAllPages } from "@/lib/fetchAllPages";
import { storagePathFromValue } from "@/lib/storageFetch";
import type {
  Activity,
  ActivityCategory,
  ActivityCreate,
  ActivityUpdate,
  AvailabilityResponse,
} from "@/types";

/** Backend cap: GET /activities `limit` le=100. */
const ACTIVITIES_PAGE_SIZE = 100;

function withMediaPaths<T extends ActivityCreate | ActivityUpdate>(data: T): T {
  return {
    ...data,
    media: data.media?.map((m) => ({
      ...m,
      url: storagePathFromValue(m.url),
      thumbnail_url: m.thumbnail_url
        ? storagePathFromValue(m.thumbnail_url)
        : m.thumbnail_url,
    })),
  };
}

export const activityService = {
  create: async (data: ActivityCreate): Promise<Activity> => {
    const response = await apiClient.post<ActivityApiPayload>(
      "/activities",
      withMediaPaths(data),
    );
    return activityFromApi(response.data);
  },

  getAll: (filters?: {
    /** Admin-only: filter by activity owner. Partners list their own activities from the session. */
    user_id?: string;
    category?: ActivityCategory;
    is_active?: boolean;
    is_visible?: boolean;
    region_id?: string;
    search?: string;
    lat?: number;
    lng?: number;
  }): Promise<Activity[]> =>
    fetchAllPages(async (page) => {
      const response = await apiClient.get<ActivityApiPayload[]>(
        "/activities",
        { params: { ...filters, ...page } },
      );
      return activitiesFromApi(response.data);
    }, ACTIVITIES_PAGE_SIZE),

  getById: async (activityId: string): Promise<Activity> => {
    const response = await apiClient.get<ActivityApiPayload>(
      `/activities/${activityId}`,
    );
    return activityFromApi(response.data);
  },

  update: async (
    activityId: string,
    data: ActivityUpdate,
  ): Promise<Activity> => {
    const response = await apiClient.patch<ActivityApiPayload>(
      `/activities/${activityId}`,
      withMediaPaths(data),
    );
    return activityFromApi(response.data);
  },

  getAvailability: async (
    activityId: string,
    params: {
      date_from: string;
      date_to: string;
      people_count?: number;
      include_all_schedule_slots?: boolean;
    },
  ): Promise<AvailabilityResponse> => {
    const response = await apiClient.get<AvailabilityResponse>(
      `/activities/${activityId}/availability`,
      { params },
    );
    return response.data;
  },
};
